import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight, Check, ShieldCheck, Users, BarChart3, Sparkles, KeyRound,
  Settings2, Lock, Layers, Workflow, CreditCard, Building2, Headphones,
  Globe, FileSignature, UserPlus, Zap, Database,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { alternateLinkList } from "@/lib/i18n/locale";
import networking from "@/assets/networking.jpg";
import heroCard from "@/assets/hero-card.webp";
import aiAbstract from "@/assets/ai-abstract.jpg";

export const Route = createFileRoute("/el/teams")({
  head: () => ({
    meta: [
      { title: "Ομάδες — Ψηφιακές κάρτες και AI CRM | Banana Cards" },
      { name: "description", content: "Εξοπλίστε κάθε εργαζόμενο με μια ψηφιακή επαγγελματική κάρτα με την εταιρική σας ταυτότητα, συγκεντρώστε κάθε υποψήφιο πελάτη σε ένα CRM και αξιοποιήστε NFC, SSO και αναλυτικά στοιχεία σε όλο τον οργανισμό σας." },
      { property: "og:title", content: "banana.cards για ομάδες" },
      { property: "og:description", content: "Μία πλατφόρμα για ψηφιακές επαγγελματικές κάρτες, καταγραφή υποψήφιων πελατών, AI CRM, συμβάσεις και αυτοματισμούς — για ομάδες πωλήσεων, εταιρείες υπηρεσιών και μεγάλες επιχειρήσεις." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/teams", "el"),
  }),
  component: TeamsPage,
});

const HERO_METRICS = [
  { v: "200k+", l: "επαγγελματίες χρησιμοποιούν το Banana" },
  { v: "65×", l: "ταχύτερο follow-up από τις χάρτινες κάρτες" },
  { v: "92%", l: "των ομάδων εξοικονομούν 4+ ώρες / εβδομάδα" },
  { v: "SOC 2", l: "Type II — έτοιμο για μεγάλες επιχειρήσεις" },
];

const TRUSTED_BY = ["Allianz", "JLL", "Northbeam", "Verta", "Pelo", "Linear", "HubSpot", "Notion"];

const PILLARS = [
  {
    icon: CreditCard,
    title: "Ψηφιακές επαγγελματικές κάρτες με την ταυτότητά σας για κάθε χρήστη",
    desc: "Ενιαία εταιρική ταυτότητα σε όλη την επιχείρηση — λογότυπο, χρώματα, σύνδεσμοι και υπογραφές σύμφωνα με το brand σας. Μία αλλαγή ενημερώνει όλες τις κάρτες.",
    points: ["Πρότυπα με κλειδωμένη εταιρική ταυτότητα", "Μαζική εισαγωγή μέσω CSV / HRIS", "Δημιουργία υπογραφής email"],
  },
  {
    icon: Users,
    title: "Κάθε υποψήφιος πελάτης σε ένα κοινό CRM",
    desc: "Όταν ένα μέλος της ομάδας αγγίζει, σαρώνει ή κοινοποιεί, η επαφή καταχωρίζεται σε έναν κοινό χώρο εργασίας — εμπλουτισμένη, χωρίς διπλοεγγραφές και στο σωστό pipeline.",
    points: ["Κοινά και ιδιωτικά pipeline", "Εμπλουτισμός υποψήφιων πελατών με AI", "Εκ περιτροπής ανάθεση"],
  },
  {
    icon: ShieldCheck,
    title: "Ασφάλεια και έλεγχος για το IT",
    desc: "SSO, διαχείριση χρηστών μέσω SCIM, πρόσβαση βάσει ρόλων, αρχεία καταγραφής ελέγχου και αναλυτικά δικαιώματα — τα δεδομένα παραμένουν δικά σας, ακόμη κι όταν κάποιος αποχωρεί.",
    points: ["SAML SSO + SCIM", "Αναλυτικοί ρόλοι και RBAC", "GDPR & SOC 2 Type II"],
  },
];

const DEEP_DIVES = [
  {
    eyebrow: "Ένταξη ομάδας",
    title: "Εξοπλίστε όλη την ομάδα",
    italic: "μέσα σε ένα απόγευμα.",
    desc: "Στείλτε προσκλήσεις μέσω CSV, Google Workspace ή του HRIS σας. Το Banana δημιουργεί αυτόματα μια κάρτα με την εταιρική σας ταυτότητα για κάθε εργαζόμενο, στέλνει το προϊόν NFC στο γραφείο του και συγχρονίζει τα προφίλ όταν αλλάζουν οι ρόλοι.",
    image: networking,
    points: [
      "Μαζικές προσκλήσεις και αυτόματη δημιουργία καρτών",
      "Συγχρονισμός με Google Workspace, Okta, BambooHR",
      "Αποχώρηση με ένα κλικ — διατήρηση δεδομένων, απενεργοποίηση θέσης χρήστη",
      "Κεντρική διαχείριση αποστολών και επαναληπτικών παραγγελιών NFC",
    ],
  },
  {
    eyebrow: "Καταγραφή υποψήφιων πελατών",
    title: "Μετατρέψτε κάθε συνάντηση",
    italic: "σε ευκαιρία στο pipeline.",
    desc: "Αγγίξτε για κοινοποίηση, σαρώστε χάρτινες κάρτες, προωθήστε email ή αφήστε μια φωνητική σημείωση — το AI του Banana καταγράφει την επαφή, την εμπλουτίζει με εταιρικά στοιχεία και συντάσσει ένα follow-up στο δικό σας ύφος.",
    image: aiAbstract,
    reverse: true,
    points: [
      "Σάρωση καρτών και ανάλυση email με AI",
      "Εμπλουτισμός με εταιρικά στοιχεία και κοινωνικά προφίλ",
      "Αυτόματα προσχέδια follow-up στο ύφος σας",
      "Έξυπνη δρομολόγηση στον σωστό εκπρόσωπο",
    ],
  },
  {
    eyebrow: "Αναλυτικά στοιχεία",
    title: "Δείτε τι αποδίδει —",
    italic: "για κάθε εκπρόσωπο.",
    desc: "Πίνακες ελέγχου σε πραγματικό χρόνο για αγγίγματα, νέες επαφές, κλεισμένες συναντήσεις και έσοδα που επηρεάστηκαν. Φιλτράρετε ανά ομάδα, περιοχή ή καμπάνια για να δείτε ποιος δημιουργεί ευκαιρίες στο pipeline και πού αξίζει να επενδύσετε περισσότερο.",
    image: heroCard,
    points: [
      "Κατατάξεις ανά εκπρόσωπο και ομάδα",
      "Προβολές διοχέτευσης μετατροπών και ομάδων χρηστών",
      "Απόδοση αποτελεσμάτων βάσει UTM",
      "Εξαγωγή σε εργαλεία BI ή Sheets",
    ],
  },
];

const PLATFORM_FEATURES = [
  { icon: CreditCard, title: "Κάρτες NFC και φορετά αξεσουάρ", desc: "Μεταλλικές, μαύρες ή εξατομικευμένες — ένα άγγιγμα μοιράζεται ολόκληρο το προφίλ." },
  { icon: Layers, title: "Πολλαπλές κάρτες ανά χρήστη", desc: "Ξεχωριστές ταυτότητες για πωλήσεις, προσλήψεις ή συγκεκριμένες εκδηλώσεις." },
  { icon: FileSignature, title: "Συμβάσεις και ηλεκτρονική υπογραφή", desc: "Στείλτε και υπογράψτε συμφωνίες μέσα από την επαφή." },
  { icon: Workflow, title: "Pipeline και εργασίες", desc: "Προωθήστε συμφωνίες με κοινές ροές εργασίας." },
  { icon: Zap, title: "Αυτοματισμοί", desc: "Ενέργειες που ενεργοποιούνται σε CRM, email και Slack." },
  { icon: BarChart3, title: "Αναλυτικά στοιχεία και εξαγωγές", desc: "Πίνακες ελέγχου σε πραγματικό χρόνο, BigQuery και Sheets." },
  { icon: Database, title: "Ανοιχτό API και webhooks", desc: "Συνδέστε το Banana με τα εργαλεία που ήδη χρησιμοποιείτε." },
  { icon: Globe, title: "Φιλοξενία σε πολλές περιοχές", desc: "Δυνατότητα αποθήκευσης δεδομένων στην ΕΕ και στις ΗΠΑ." },
];

const SECURITY = [
  { icon: KeyRound, title: "SAML SSO", desc: "Okta, Google, Azure AD και κάθε πάροχος ταυτότητας SAML 2.0." },
  { icon: UserPlus, title: "Διαχείριση χρηστών μέσω SCIM", desc: "Αυτόματος συγχρονισμός χρηστών και ομάδων." },
  { icon: Lock, title: "Πρόσβαση βάσει ρόλων", desc: "Ιδιοκτήτης, διαχειριστής, υπεύθυνος, μέλος, επισκέπτης." },
  { icon: ShieldCheck, title: "SOC 2 Type II + GDPR", desc: "Ανεξάρτητος έλεγχος, με επιλογή φιλοξενίας στην ΕΕ." },
  { icon: Settings2, title: "Αρχεία καταγραφής ελέγχου", desc: "Κάθε ενέργεια εξάγεται στο SIEM σας." },
  { icon: Building2, title: "Προσαρμοσμένα DPA και MSA", desc: "Έτοιμη νομική τεκμηρίωση στα εταιρικά πακέτα." },
];

const USE_CASES = [
  { title: "Ομάδες πωλήσεων", desc: "Καταγράψτε υποψήφιους πελάτες σε εκδηλώσεις, εμπλουτίστε αυτόματα τα στοιχεία τους και αναθέστε τους στον σωστό εκπρόσωπο πριν προλάβει ο ανταγωνισμός.", href: "/el/solutions/sales" },
  { title: "Εταιρείες υπηρεσιών και σύμβουλοι", desc: "Δώστε σε κάθε σύμβουλο μια επαγγελματική ταυτότητα και πρόσβαση σε κοινό πελατολόγιο.", href: "/el/solutions/founders" },
  { title: "Ομάδες ακινήτων", desc: "Μείνετε στο μυαλό κάθε αγοραστή, πωλητή και συνεργάτη, για όλα τα ακίνητά σας.", href: "/el/solutions/real-estate" },
  { title: "Μεγάλες επιχειρήσεις", desc: "SSO, SCIM, αρχεία καταγραφής ελέγχου και χρέωση ανά χρήστη για οργανισμούς με 500+ άτομα.", href: "/el/solutions/enterprise" },
];

const STEPS = [
  { title: "Κλείστε μια παρουσίαση", desc: "30 λεπτά με έναν ειδικό για να σχεδιάσετε την υλοποίησή σας." },
  { title: "Προσκαλέστε και προσαρμόστε", desc: "Εντάσσουμε την ομάδα σας και κλειδώνουμε το πρότυπο εταιρικής ταυτότητας." },
  { title: "Παραλάβετε τις κάρτες NFC", desc: "Μαζικές αποστολές απευθείας σε θέσεις εργασίας ή γραφεία, παγκοσμίως." },
  { title: "Μετρήστε και αναπτυχθείτε", desc: "Παρακολουθήστε υποψήφιους πελάτες και έσοδα που επηρεάστηκαν από την πρώτη μέρα." },
];

const FAQ = [
  { q: "Σε τι διαφέρει το Teams από το δωρεάν πακέτο;", a: "Το Teams προσθέτει κοινό χώρο εργασίας, πρότυπα με κλειδωμένη εταιρική ταυτότητα, μαζικές προσκλήσεις, SSO, πρόσβαση βάσει ρόλων, αναλυτικά στοιχεία ομάδας και υποστήριξη προτεραιότητας. Το Enterprise προσθέτει SCIM, αρχεία καταγραφής ελέγχου, προσαρμοσμένο DPA και αποκλειστικό υπεύθυνο επιτυχίας πελατών." },
  { q: "Μπορούμε να διατηρήσουμε το υπάρχον CRM μας;", a: "Ναι. Το Banana συνδέεται με Salesforce, HubSpot, Pipedrive, Attio και άλλα συστήματα μέσω εγγενών ενσωματώσεων ή webhooks. Χρησιμοποιήστε το Banana για την καταγραφή επαφών και το CRM σας ως κεντρικό σύστημα δεδομένων — ή χρησιμοποιήστε το Banana αυτόνομα." },
  { q: "Στέλνετε κάρτες NFC σε όλο τον κόσμο;", a: "Ναι. Αποστέλλουμε από κέντρα στην ΕΕ και στις ΗΠΑ σε περισσότερες από 100 χώρες, με κεντρική διαχείριση επαναληπτικών παραγγελιών από τον πίνακα ελέγχου της ομάδας σας." },
  { q: "Τι συμβαίνει όταν κάποιος αποχωρεί από την ομάδα;", a: "Με ένα κλικ απενεργοποιείται η θέση χρήστη: η κάρτα σταματά να κοινοποιεί στοιχεία, οι επαφές παραμένουν στον χώρο εργασίας και η ιδιοκτησία τους μεταβιβάζεται σε άλλο μέλος της ομάδας. Κανένα δεδομένο δεν χάνεται." },
  { q: "Συμμορφώνεται το Banana με το GDPR και το SOC 2;", a: "Ναι — έχει ελεγχθεί κατά SOC 2 Type II και συμμορφώνεται με το GDPR, με επιλογή αποθήκευσης δεδομένων αποκλειστικά στην ΕΕ στα εταιρικά πακέτα." },
];

function TeamsPage() {
  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-grid mask-fade-edges" aria-hidden />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-14 pt-16 md:grid-cols-12 md:px-6 md:pt-24">
          <div className="md:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              banana.cards για ομάδες
            </p>
            <h1 className="mt-6 max-w-3xl font-display leading-[1.02] tracking-[-0.02em] md:text-[5.25rem] md:leading-[0.98] font-bold text-5xl">
              Μία πλατφόρμα. Κάθε εργαζόμενος.{" "}
              <em className="italic">Κάθε υποψήφιος πελάτης καταγράφεται.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Δώστε σε όλη την ομάδα ψηφιακές επαγγελματικές κάρτες με την ταυτότητά σας, προϊόντα NFC και ένα AI CRM —
              με SSO, αναλυτικά στοιχεία και εργαλεία διαχείρισης που εγκρίνει και το IT.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/el/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90"
              >
                Κλείστε μια παρουσίαση
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/el/pricing"
                className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/80"
              >
                Δείτε τιμές για ομάδες
              </Link>
            </div>
            <dl className="mt-12 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-6 border-t border-foreground/10 pt-8 md:grid-cols-4">
              {HERO_METRICS.map((m) => (
                <div key={m.l}>
                  <dt className="font-display text-3xl leading-none md:text-4xl">{m.v}</dt>
                  <dd className="mt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {m.l}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative md:col-span-5">
            <div className="absolute -inset-8 -z-10 rounded-[2.5rem] bg-foreground/5 blur-3xl" aria-hidden />
            <div className="relative overflow-hidden rounded-[2rem] bg-muted">
              <img src={networking} alt="Μια ομάδα χρησιμοποιεί το banana.cards" loading="eager" width={1600} height={2000} className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="absolute -left-6 top-10 hidden rounded-2xl bg-background p-3 md:flex md:items-center md:gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-lime">
                <Users className="h-4 w-4" />
              </span>
              <div className="pr-1">
                <p className="text-[11px] font-bold leading-none">42 θέσεις χρήστη ενεργοποιήθηκαν</p>
                <p className="mt-1 text-[10.5px] text-muted-foreground">μέσω Okta SCIM</p>
              </div>
            </div>
            <div className="absolute -right-4 bottom-6 hidden rounded-2xl bg-background p-3 md:block">
              <p className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">Νέοι υποψήφιοι πελάτες Γ΄ τριμήνου</p>
              <p className="mt-1 font-display text-2xl leading-none">3.184</p>
            </div>
          </div>
        </div>

        <div className="bg-muted py-7">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <p className="text-center text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              Μας εμπιστεύονται ομάδες πωλήσεων, εταιρείες υπηρεσιών και μεγάλες επιχειρήσεις σε 100+ χώρες
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
              {TRUSTED_BY.map((n) => (
                <span key={n} className="text-lg font-bold tracking-tight text-foreground/40">{n}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-12 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Γιατί οι ομάδες επιλέγουν το Banana</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Τα εργαλεία δικτύωσης που η ομάδα σας{" "}
            <em className="italic">θα χρησιμοποιεί πραγματικά.</em>
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-2xl bg-muted p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-6 text-xl font-bold leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <ul className="mt-5 grid gap-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                      <span className="font-medium">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => (
        <section key={d.title} className={i % 2 === 0 ? "bg-muted" : "bg-background"}>
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:px-6">
            <div className={`relative ${d.reverse ? "md:order-2" : ""}`}>
              <div className="absolute -inset-6 rounded-[2rem] bg-foreground/5 blur-3xl" aria-hidden />
              <img src={d.image} alt={d.title} loading="lazy" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
            </div>
            <div className={d.reverse ? "md:order-1" : ""}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{d.eyebrow}</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                {d.title} <em className="italic">{d.italic}</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">{d.desc}</p>
              <ul className="mt-6 grid gap-2.5">
                {d.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" /> <span className="font-medium">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-12 flex flex-col items-start gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Όλα περιλαμβάνονται</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Μία άδεια χρήσης. <em className="italic">Ολόκληρη η πλατφόρμα.</em>
            </h2>
          </div>
          <Link to="/el/pricing" className="inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline">
            Συγκρίνετε πακέτα <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PLATFORM_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl bg-muted p-5">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
                <p className="mt-4 text-sm font-bold">{f.title}</p>
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      <section className="bg-brand-dark text-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 md:grid-cols-12 md:px-6">
          <div className="md:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-lime">Ασφάλεια και συμμόρφωση</p>
            <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
              Σχεδιασμένο για το IT. <em className="italic">Με την έγκριση του νομικού τμήματος.</em>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-background/70">
              Το Banana προσφέρει τους ελέγχους που απαιτούν οι μεγάλες επιχειρήσεις — και την τεκμηρίωση που το αποδεικνύει.
            </p>
            <Link
              to="/el/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground"
            >
              Μιλήστε με την ομάδα ασφάλειας <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-3 md:col-span-7 sm:grid-cols-2">
            {SECURITY.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-2xl bg-primary-foreground/[0.06] p-5 backdrop-blur">
                  <Icon className="h-5 w-5 text-brand-lime" strokeWidth={1.75} />
                  <p className="mt-4 text-sm font-bold">{s.title}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-background/65">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Για κάθε ομάδα</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Όπως κι αν δημιουργεί επαφές η ομάδα σας, <em className="italic">έχουμε τη λύση.</em>
          </h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {USE_CASES.map((u) => (
            <Link
              key={u.href}
              to={u.href}
              aria-label={`${u.title} — μάθετε περισσότερα για αυτήν την περίπτωση χρήσης`}
              className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70"
            >
              <p className="text-base font-bold">{u.title}</p>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{u.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                Μάθετε περισσότερα <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-24 text-center md:px-6">
          <Sparkles className="mx-auto h-6 w-6" />
          <p className="mx-auto mt-6 max-w-3xl font-display text-3xl leading-tight md:text-5xl">
            «Δώσαμε το Banana σε 120 εργαζομένους μέσα σε μία εβδομάδα. Η καταγραφή υποψήφιων πελατών σε συνέδρια έγινε{" "}
            <em className="italic">65 φορές ταχύτερη</em> — και το pipeline μας δεν ήταν ποτέ πιο οργανωμένο.»
          </p>
          <p className="mt-8 text-sm font-bold">Priya Sharma</p>
          <p className="text-xs text-muted-foreground">Αντιπρόεδρος Πωλήσεων, Northbeam</p>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Ξεκινήστε σε 4 βήματα</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Σε λειτουργία σε μία εβδομάδα, <em className="italic">όχι σε ένα τρίμηνο.</em>
          </h2>
        </div>
        <ol className="mt-12 grid gap-4 md:grid-cols-4">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative rounded-2xl bg-muted p-6">
              <span className="absolute -top-3 left-6 inline-flex h-7 items-center justify-center rounded-full bg-foreground px-3 text-[11px] font-bold text-background">
                Βήμα {i + 1}
              </span>
              <h3 className="mt-3 text-base font-bold">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-24 md:px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Συχνές ερωτήσεις</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Οι ερωτήσεις σας, <em className="italic">με απαντήσεις.</em>
          </h2>
        </div>
        <div className="divide-y divide-foreground/10 rounded-2xl bg-card">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-bold">
                {f.q}
                <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-brand-dark px-8 py-16 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.12),transparent_55%)]" aria-hidden />
          <Headphones className="mx-auto h-6 w-6 text-brand-lime" />
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
            Είστε έτοιμοι να δώσετε το Banana στην ομάδα σας{" "}
            <em className="italic text-brand-lime">αυτή την εβδομάδα;</em>
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
            Παρουσίαση 30 λεπτών με έναν ειδικό. Εξατομικευμένη προσφορά για 5 έως 5.000 χρήστες. Χωρίς πίεση.
          </p>
          <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/el/contact" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
              Κλείστε μια παρουσίαση
            </Link>
            <Link to="/el/pricing" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
              Δείτε τιμές για ομάδες
            </Link>
          </div>
        </div>
        </div>
      </section>
    </SiteLayout>
  );
}